import { Injectable, Injector } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, catchError, switchMap, timeout, debounceTime } from 'rxjs/operators';
import * as EventsActions from './events.actions';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environment/environment';
import { EventDTO } from '../../dto/event/event.dto';
import { AuthService } from '../../services/auth/auth.service';

@Injectable()
export class EventsEffects {
  constructor(
    private actions$: Actions,
    private http: HttpClient,
    private injector: Injector
  ) {}

  loadCalendarEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventsActions.loadCalendarEvents),
      debounceTime(80),
      switchMap(({ queryParams }) => {
        
        // Get AuthService to check authentication state
        const authService = this.injector.get(AuthService);
        
        const successQueryParams = { ...(queryParams || {}) } as Record<string, unknown>;
        const rawParams = { ...(queryParams || {}) } as Record<string, unknown>;
        if (rawParams['exclude_group_events'] === undefined) {
          rawParams['exclude_group_events'] = 'true';
        }
        const excludeGroupEvents =
          rawParams['exclude_group_events'] === 'true' ||
          rawParams['exclude_group_events'] === true;

        const httpQueryParams: Record<string, string> = {};
        for (const [key, value] of Object.entries(rawParams)) {
          if (value !== undefined && value !== null && value !== '') {
            httpQueryParams[key] = String(value);
          }
        }

        return this.http.get<EventDTO[]>(`${environment.apiUrl}/events`, {
          params: httpQueryParams,
        }).pipe(
          timeout(30000), // 30 second timeout to prevent hanging observables
          map(events => {
            if (!events || !Array.isArray(events)) {
              return EventsActions.loadCalendarEventsSuccess({ data: [], queryParams: successQueryParams });
            }
            
            // Filter out 'exercise' events if user is not authenticated
            let filteredEvents: EventDTO[] = events;
            if (!authService.isAuthenticated) {
              filteredEvents = events.filter((event) => event.event_type !== 'exercise');
            }

            // Filter out events that are not shown for non-authenticated users
            if (!authService.isAuthenticated) {
              filteredEvents = filteredEvents.filter((event) => event.is_shown !== false);
            }

            if (excludeGroupEvents) {
              filteredEvents = filteredEvents.filter(
                (event) => event.user_group_id == null || Number(event.user_group_id) <= 0
              );
            }
            
            return EventsActions.loadCalendarEventsSuccess({
              data: filteredEvents,
              queryParams: successQueryParams,
            });
          }),
          catchError(error => {
            console.error('[EventsEffects] Error loading calendar events:', error);
            // CRITICAL: Treat 404 (No events found) as success with empty array, not as error
            // This prevents the UI from showing error state when filters result in no events
            if (error?.status === 404) {
              return of(EventsActions.loadCalendarEventsSuccess({
                data: [],
                queryParams: successQueryParams,
              }));
            }
            // For other errors, dispatch failure
            return of(EventsActions.loadCalendarEventsFailure({ error }));
          })
        );
      })
    )
  );
}

